import { useState } from 'react';
import {
  Typography, List, Badge, Button, Space, Tag, Tooltip,
} from 'antd';
import {
  BellOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  FileTextOutlined,
  TeamOutlined,
  AlertOutlined,
  CalendarOutlined,
  UploadOutlined,
  CommentOutlined,
  UserOutlined,
  UserAddOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification, NotificationType } from '@/types/common';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';

const { Text } = Typography;

// ── Icon + colour per notification type ────────────────────────────────────

const TYPE_META: Record<NotificationType, { icon: React.ReactNode; color: string; label: string }> = {
  PHASE_TRANSITION: {
    icon: <SwapOutlined />,
    color: '#4F46E5',
    label: 'Phase Transition',
  },
  REVIEW_REQUEST: {
    icon: <TeamOutlined />,
    color: '#0284C7',
    label: 'Review Request',
  },
  REVIEW_COMPLETED: {
    icon: <CheckCircleOutlined />,
    color: '#16A34A',
    label: 'Review Completed',
  },
  MILESTONE_DUE: {
    icon: <CalendarOutlined />,
    color: '#D97706',
    label: 'Milestone Due',
  },
  BID_DECISION: {
    icon: <FileTextOutlined />,
    color: '#7C3AED',
    label: 'Bid Decision',
  },
  SUBMISSION_REMINDER: {
    icon: <AlertOutlined />,
    color: '#DC2626',
    label: 'Submission Reminder',
  },
  QA_RECEIVED: {
    icon: <CommentOutlined />,
    color: '#0891B2',
    label: 'Q&A Received',
  },
  DOCUMENT_UPLOADED: {
    icon: <UploadOutlined />,
    color: '#059669',
    label: 'Document Uploaded',
  },
  COMMENT_ADDED: {
    icon: <CommentOutlined />,
    color: '#6B7280',
    label: 'Comment',
  },
  TASK_ASSIGNED: {
    icon: <UserOutlined />,
    color: '#B45309',
    label: 'Task Assigned',
  },
  SYSTEM_ALERT: {
    icon: <AlertOutlined />,
    color: '#6B7280',
    label: 'System',
  },
  REGISTRATION_REQUEST: {
    icon: <UserAddOutlined />,
    color: '#7C3AED',
    label: 'Access Request',
  },
  REGISTRATION_APPROVED: {
    icon: <CheckCircleOutlined />,
    color: '#16A34A',
    label: 'Access Approved',
  },
  REGISTRATION_REJECTED: {
    icon: <CloseCircleOutlined />,
    color: '#DC2626',
    label: 'Access Rejected',
  },
  ROLE_REQUEST: {
    icon: <UserAddOutlined />,
    color: '#7C3AED',
    label: 'Role Request',
  },
  ROLE_REQUEST_APPROVED: {
    icon: <CheckCircleOutlined />,
    color: '#16A34A',
    label: 'Role Approved',
  },
  ROLE_REQUEST_REJECTED: {
    icon: <CloseCircleOutlined />,
    color: '#DC2626',
    label: 'Role Rejected',
  },
};

const formatRelativeTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationItem = ({
  item,
  onMarkRead,
}: {
  item: Notification;
  onMarkRead: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const meta = TYPE_META[item.type] ?? TYPE_META.SYSTEM_ALERT;

  const handleGoTo = () => {
    if (!item.read) onMarkRead(item.id);
    if (item.type === 'REGISTRATION_REQUEST') {
      navigate('/admin/users?tab=registrations');
    } else if (item.type === 'ROLE_REQUEST') {
      navigate('/admin/users?tab=role-requests');
    } else if (item.type === 'ROLE_REQUEST_APPROVED' || item.type === 'ROLE_REQUEST_REJECTED') {
      navigate('/profile');
    } else if (item.referenceType === 'OPPORTUNITY' && item.referenceId) {
      navigate(`/opportunities/${item.referenceId}`);
    } else if (item.referenceType === 'DISCUSSION_THREAD' && item.referenceId) {
      navigate(`/discussions?thread=${item.referenceId}`);
    } else if (item.link) {
      window.open(item.link, '_blank');
    }
  };

  const hasLink =
    item.type === 'REGISTRATION_REQUEST' ||
    item.type === 'ROLE_REQUEST' ||
    item.type === 'ROLE_REQUEST_APPROVED' ||
    item.type === 'ROLE_REQUEST_REJECTED' ||
    (item.referenceType === 'OPPORTUNITY' && item.referenceId) ||
    (item.referenceType === 'DISCUSSION_THREAD' && item.referenceId) ||
    item.link;

  return (
    <List.Item
      onClick={() => !item.read && onMarkRead(item.id)}
      style={{
        padding: '12px 16px',
        cursor: item.read ? 'default' : 'pointer',
        background: item.read ? 'transparent' : 'var(--color-primary-light)',
        borderLeft: item.read ? '3px solid transparent' : '3px solid var(--color-primary)',
        borderRadius: 10,
        marginBottom: 6,
        transition: 'background 0.15s',
      }}
    >
      {/* Type icon badge */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: `${meta.color}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 16,
          color: meta.color,
          marginRight: 12,
        }}
      >
        {meta.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
              <Tag
                style={{
                  fontSize: 10,
                  padding: '0 5px',
                  lineHeight: '18px',
                  background: `${meta.color}18`,
                  color: meta.color,
                  border: 'none',
                  marginRight: 0,
                }}
              >
                {meta.label}
              </Tag>
              {!item.read && (
                <Badge color="#4F46E5" style={{ marginLeft: 2 }} />
              )}
            </div>
            <Text
              strong={!item.read}
              style={{ fontSize: 13, display: 'block', color: item.read ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}
            >
              {item.title}
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 2 }}
            >
              {item.message}
            </Text>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
              {formatRelativeTime(item.createdAt)}
            </Text>

            <Space size={4}>
              {hasLink && (
                <Tooltip title={
                  item.type === 'REGISTRATION_REQUEST' ? 'Review Request' :
                  item.referenceType === 'OPPORTUNITY' ? 'Go to Opportunity' :
                  item.referenceType === 'DISCUSSION_THREAD' ? 'Go to Discussion' : 'View'
                }>
                  <Button
                    type="link"
                    size="small"
                    icon={<ArrowRightOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleGoTo(); }}
                    style={{ fontSize: 11, padding: '0 4px', height: 'auto', color: 'var(--color-purple)' }}
                  >
                    {item.type === 'REGISTRATION_REQUEST' ? 'Review Request' :
                     item.referenceType === 'OPPORTUNITY' ? 'Go to RFO' :
                     item.referenceType === 'DISCUSSION_THREAD' ? 'Go to Discussion' : 'View'}
                  </Button>
                </Tooltip>
              )}
              {!item.read && (
                <Tooltip title="Mark as read">
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={(e) => { e.stopPropagation(); onMarkRead(item.id); }}
                    style={{ fontSize: 11, padding: '0 4px', height: 'auto', color: 'var(--color-text-secondary)' }}
                  />
                </Tooltip>
              )}
            </Space>
          </div>
        </div>
      </div>
    </List.Item>
  );
};

const NotificationsPage = () => {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');

  const filtered = activeTab === 'unread'
    ? notifications.filter((n) => !n.read)
    : notifications;

  return (
    <div>
      <PageHeader
        title={
          <Space size={8} align="center">
            <BellOutlined style={{ fontSize: 20, color: 'var(--color-primary)' }} />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Tag color="geekblue" style={{ fontWeight: 600 }}>{unreadCount} unread</Tag>
            )}
          </Space>
        }
        subtitle="Stay on top of phase changes, reviews, mentions and access requests"
        actions={
          unreadCount > 0 ? (
            <Button
              type="text"
              icon={<CheckCircleOutlined />}
              onClick={() => markAllAsRead()}
              style={{ color: 'var(--color-primary)', fontSize: 13 }}
            >
              Mark all as read
            </Button>
          ) : undefined
        }
      />

      {/* Tab toggle as chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="ui-chip ui-focusable"
          data-active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
        >
          All <span className="ui-chip__count">{notifications.length}</span>
        </button>
        <button
          type="button"
          className="ui-chip ui-focusable"
          data-active={activeTab === 'unread'}
          onClick={() => setActiveTab('unread')}
        >
          Unread
          {unreadCount > 0 && <span className="ui-chip__count">{unreadCount}</span>}
        </button>
      </div>

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div className="ui-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="ui-skeleton" style={{ width: '40%', height: 12, marginBottom: 8 }} />
                  <div className="ui-skeleton" style={{ width: '75%', height: 12 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<BellOutlined />}
            title={activeTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            description={
              activeTab === 'unread'
                ? "You're all caught up — new unread notifications will appear here."
                : "When something needs your attention, it'll show up here."
            }
          />
        ) : (
          <div style={{ padding: '8px 12px' }}>
            <List
              className="ui-stagger"
              dataSource={filtered}
              renderItem={(item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  onMarkRead={markAsRead}
                />
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
